// apps/web/src/pages/Products.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, RefreshCw, Edit, Trash2, Image, Package, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import pb from '../lib/pocketbaseClient';

// --- Universal image URL helper (works with any SDK version) ---
const getImageUrl = (product, imageName) => {
  if (!imageName || !product) return null;
  try {
    // Try official methods
    if (pb.files && typeof pb.files.getURL === 'function') {
      return pb.files.getURL(product, imageName);
    }
    if (pb.files && typeof pb.files.getUrl === 'function') {
      return pb.files.getUrl(product, imageName);
    }
    if (typeof pb.getFileUrl === 'function') {
      return pb.getFileUrl(product, imageName);
    }
    // Manual fallback
    const base = pb.baseUrl || '';
    const collection = product.collectionId || product.collectionName || 'products';
    return `${base}/api/files/${collection}/${product.id}/${imageName}`;
  } catch {
    return null;
  }
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [saleCounts, setSaleCounts] = useState({});
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const records = await pb.collection('products').getFullList({
        sort: '-created',
      });
      setProducts(records);

      const orders = await pb.collection('orders').getFullList({
        filter: 'status = "Paid" || status = "Delivered"',
        fields: 'items',
      });
      const counts = {};
      orders.forEach((order) => {
        const items = order.items || [];
        items.forEach((item) => {
          if (item.id) {
            counts[item.id] = (counts[item.id] || 0) + (item.quantity || 0);
          }
        });
      });
      setSaleCounts(counts);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await pb.collection('products').delete(id);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const filteredProducts = useMemo(() => {
    if (statusFilter === 'all') return products;
    return products.filter((p) => p.status === statusFilter);
  }, [products, statusFilter]);

  const counts = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.status === 'active').length;
    const inactive = products.filter((p) => p.status === 'inactive').length;
    return { total, active, inactive };
  }, [products]);

  // Get thumbnail – try featuredImage first, then first gallery image
  const getThumbnail = (product) => {
    if (!product) return null;
    let imageFile = null;
    if (product.featuredImage) {
      imageFile = product.featuredImage;
    } else if (product.galleryImages && product.galleryImages.length > 0) {
      imageFile = product.galleryImages[0];
    }
    if (!imageFile) return null;
    return getImageUrl(product, imageFile);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Product Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => navigate('/products/new')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add New
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('all')}
          size="sm"
          className="gap-2"
        >
          <Package className="w-4 h-4" />
          All
          <span className="ml-1 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded-full text-xs">
            {counts.total}
          </span>
        </Button>
        <Button
          variant={statusFilter === 'active' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('active')}
          size="sm"
          className="gap-2"
        >
          <Eye className="w-4 h-4" />
          Active
          <span className="ml-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
            {counts.active}
          </span>
        </Button>
        <Button
          variant={statusFilter === 'inactive' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('inactive')}
          size="sm"
          className="gap-2"
        >
          <Eye className="w-4 h-4" />
          Inactive
          <span className="ml-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full text-xs">
            {counts.inactive}
          </span>
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">Loading...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border">
          <p className="text-slate-500">No products found. Click "Add New" to create one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Image</th>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Category</th>
                <th className="px-4 py-3 text-left font-medium">Bedroom</th>
                <th className="px-4 py-3 text-left font-medium">Area</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Floor Plan ₹</th>
                <th className="px-4 py-3 text-left font-medium">Full Design ₹</th>
                <th className="px-4 py-3 text-left font-medium">Sales</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => {
                const thumb = getThumbnail(p);
                const saleQty = saleCounts[p.id] || 0;
                return (
                  <tr key={p.id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={p.name}
                          className="w-10 h-10 object-cover rounded border border-slate-200"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            const parent = e.target.parentNode;
                            const fallback = document.createElement('div');
                            fallback.className = 'w-10 h-10 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-slate-400';
                            fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
                            parent.appendChild(fallback);
                            e.target.remove();
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-slate-400">
                          <Image className="w-5 h-5" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3">{p.category || '-'}</td>
                    <td className="px-4 py-3">{p.bedroom_type || '-'}</td>
                    <td className="px-4 py-3">{p.area ? `${p.area} sqft` : '-'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          p.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{p.floorPlanPrice ? `₹${p.floorPlanPrice}` : '-'}</td>
                    <td className="px-4 py-3">{p.fullDesignPrice ? `₹${p.fullDesignPrice}` : '-'}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-indigo-600">{saleQty}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/products/edit/${p.id}`)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Products;